import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, IconButton, useMediaQuery } from '@mui/material';
import { NotificationsRounded } from '@mui/icons-material';
import { arrayOf, func, shape } from 'prop-types';
import { styled } from '@mui/material/styles';

// Context
import { AuthContext } from 'authentication/AuthContext';

import DesktopNotifications from './DesktopNotifications';
import MobileNavNotifications from './MobileNavNotifications';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-anchorOriginTopRightRectangular': {
    backgroundColor: theme.palette.success.main,
    borderRadius: '50%',
    height: 8,
    right: 4,
    top: 6,
    width: 8,
    [theme.breakpoints.up('lg')]: {
      height: 9,
      right: 6,
      top: 7,
      width: 9,
    },
  },
}));

const Notifications = ({ fetchNotifications, notifications }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  // controls if the notification center is open
  const [openNotif, setOpenNotif] = useState(false);
  const notificationButtonAnchor = useRef();
  const hasNotifications = !!notifications.length;

  // roles to be used throughout component
  const { affiliates, affiliateRoles, roles: inputRoles } = useContext(AuthContext);

  const roles = useMemo(
    () => (affiliates.length ? [...inputRoles, ...affiliateRoles] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [affiliates.length]
  );

  useEffect(() => {
    if (roles.length) {
      fetchNotifications(roles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  useEffect(() => {
    setInterval(() => {
      fetchNotifications(roles);
    }, 300000);
    // Roles are initially empty
    // If roles are excluded from dependency array, interval will not work and notifications are not re-fetched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  return (
    <>
      <IconButton
        className="heap-button"
        label="Notifications"
        onClick={() => setOpenNotif(currentState => !currentState)}
        ref={notificationButtonAnchor}
        size="medium"
        sx={{
          '&:hover': {
            backgroundColor: { lg: 'secondary.light' },
          },
          borderRadius: 0,
          color: 'surface.white',
          height: 48,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          marginRight: { xs: '10px', lg: 0 },
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          width: { xs: 32, lg: 48 },
        }}
      >
        <StyledBadge invisible={!hasNotifications} variant="dot">
          <NotificationsRounded
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              color: { xs: 'inherit', lg: 'primary.dark' },
              fontSize: '28px',
            }}
          />
        </StyledBadge>
      </IconButton>
      {lgUp ? (
        <DesktopNotifications
          notificationButtonAnchor={notificationButtonAnchor.current}
          notifications={notifications}
          open={openNotif}
          setOpen={setOpenNotif}
        />
      ) : (
        <MobileNavNotifications
          notifications={notifications}
          open={openNotif}
          setOpen={setOpenNotif}
        />
      )}
    </>
  );
};

Notifications.propTypes = {
  fetchNotifications: func.isRequired,
  notifications: arrayOf(shape({})),
};
Notifications.defaultProps = {
  notifications: [],
};

export default Notifications;
